/**
 * Reusable Recharts area/line trend wrapper. Covers most "X-over-time" use
 * cases on the Overview and Engagement tabs. For anything fancier (cohort
 * heatmaps, Sankey) drop down to visx.
 */
import React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CATEGORICAL, CEDAR } from '../theme';
import { formatCompact } from '../format';

export interface Series {
  key: string;
  label: string;
  color?: string;
}

export interface TrendChartProps {
  data: Array<Record<string, unknown>>;
  xKey: string;
  series: Series[];
  type?: 'area' | 'line';
  height?: number;
  /** Format ticks on the Y axis — defaults to compact (1.2k, 3.4M). */
  yFormat?: (v: number) => string;
  /** Format labels on the X axis. Defaults to value as-is. */
  xFormat?: (v: string) => string;
  showLegend?: boolean;
  /** For area chart: enable gradient fills (default on). */
  gradient?: boolean;
  /** Optional stacking (area chart only). */
  stacked?: boolean;
}

export const TrendChart: React.FC<TrendChartProps> = ({
  data,
  xKey,
  series,
  type = 'area',
  height = 260,
  yFormat = formatCompact,
  xFormat = (v) => v,
  showLegend = true,
  gradient = true,
  stacked = false,
}) => {
  const resolvedSeries = series.map((s, i) => ({
    ...s,
    color: s.color ?? CATEGORICAL[i % CATEGORICAL.length],
  }));

  const axisStyle = {
    fontSize: 11,
    fill: 'var(--text-secondary)',
    fontFamily: 'Inter, system-ui, sans-serif',
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      {type === 'area' ? (
        <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          {gradient && (
            <defs>
              {resolvedSeries.map((s) => (
                <linearGradient key={s.key} id={`grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={s.color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={s.color} stopOpacity={0.02} />
                </linearGradient>
              ))}
            </defs>
          )}
          <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-muted)" vertical={false} />
          <XAxis
            dataKey={xKey}
            tick={axisStyle}
            axisLine={{ stroke: 'var(--border)' }}
            tickLine={false}
            tickFormatter={xFormat}
            minTickGap={32}
          />
          <YAxis
            tick={axisStyle}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => yFormat(Number(v))}
            width={44}
          />
          <Tooltip
            contentStyle={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 12,
              boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
            }}
            labelStyle={{ fontWeight: 600, color: 'var(--text-primary)' }}
            formatter={(value: number | string) =>
              typeof value === 'number' ? yFormat(value) : value
            }
            labelFormatter={(label) => xFormat(String(label))}
          />
          {showLegend && (
            <Legend
              iconType="circle"
              wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              formatter={(v) => (
                <span className="text-gray-600">{v}</span>
              )}
            />
          )}
          {resolvedSeries.map((s) => (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={s.color}
              strokeWidth={2}
              fill={gradient ? `url(#grad-${s.key})` : s.color}
              fillOpacity={gradient ? 1 : 0.15}
              stackId={stacked ? 'stack' : undefined}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      ) : (
        <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-muted)" vertical={false} />
          <XAxis
            dataKey={xKey}
            tick={axisStyle}
            axisLine={{ stroke: 'var(--border)' }}
            tickLine={false}
            tickFormatter={xFormat}
            minTickGap={32}
          />
          <YAxis
            tick={axisStyle}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => yFormat(Number(v))}
            width={44}
          />
          <Tooltip
            contentStyle={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 12,
              boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
            }}
            labelStyle={{ fontWeight: 600, color: 'var(--text-primary)' }}
            formatter={(value: number | string) =>
              typeof value === 'number' ? yFormat(value) : value
            }
            labelFormatter={(label) => xFormat(String(label))}
          />
          {showLegend && (
            <Legend
              iconType="circle"
              wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              formatter={(v) => (
                <span className="text-gray-600">{v}</span>
              )}
            />
          )}
          {resolvedSeries.map((s) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={s.color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, stroke: s.color, strokeWidth: 2, fill: 'var(--surface)' }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      )}
    </ResponsiveContainer>
  );
};

export { CEDAR };
